import { Box, Button, IconButton, Tooltip } from '@mui/material';
import { MaterialReactTable } from 'material-react-table';
import { MRT_Localization_PT } from 'material-react-table/locales/pt';
import React, { useEffect, useState } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { MdiIcon } from '../MdiIcon';
import { mdiDownload, mdiFilterOff, mdiFilterVariant, mdiFilterVariantRemove, mdiViewColumn } from '@mdi/js';
import { PaginationFilterType } from '@types';
import { FieldsDialog } from './dialog';
// Helper functions for dealing with storage data
const tryParse = (s) => {
    if (!s)
        return null;
    try {
        return JSON.parse(s);
    }
    catch {
        return null;
    }
};
const loadFresh = (cols) => {
    const keys = cols.map(c => String(c.accessorKey));
    const sizes = cols.map(c => c.size ?? 100);
    return {
        visibility: Object.fromEntries(cols.map(c => [String(c.accessorKey), c.visibility ?? true])),
        size: Object.fromEntries(keys.map((k, i) => [k, sizes[i]])),
        order: keys
        // sorting: [], // commented for now
    };
};
const normalize = (raw, cols) => {
    const keys = cols.map(c => String(c.accessorKey));
    // visibility: maintain existing preferences, new columns use c.visibility or true
    const vis = {};
    cols.forEach(c => {
        const k = String(c.accessorKey);
        vis[k] = raw?.visibility?.[k] ?? c.visibility ?? true;
    });
    // order: keep saved order for known columns, append new ones
    const savedOrder = (raw?.order ?? []);
    const order = [...savedOrder.filter(k => keys.includes(k)), ...keys.filter(k => !savedOrder.includes(k))];
    const si = {};
    keys.forEach(k => (si[k] = raw?.size?.[k] ?? cols.find(c => c.accessorKey === k)?.size ?? 100));
    // sorting: keep only valid ids
    // const sorting = (raw?.sorting ?? []).filter(s => keys.includes(s.id));
    //
    // return { visibility: vis, order, sorting };
    return { visibility: vis, order, size: si };
};
export function Table({ storageKey, data, columns, onExport, onPaginate, onSort, onFilter, onDoubleClick, selectable, selectActions, isLoading, options, cellActionItems, withRowStyle, initial, actionItems, toolbarCustomActions, customTableOption, rowActionSize, quickFilterPresets = [], initialActivePresetsKeys = [] }) {
    data = data ?? {
        data: [],
        meta: { currentPage: 0, itemsPerPage: 20, totalItems: 0, totalPages: 0 },
        links: {}
    };
    options = options ?? { filter: [], sort: [] };
    const [open, setOpen] = useState(false);
    const [cols, setCols] = useState(columns);
    const [showFilter, setShowFilter] = useState(true);
    const [columnFilterFns, setColumnFilterFns] = useState({});
    const [selected, setSelected] = useState({});
    const [columnFilter, setColumnFilter] = useState(() => (initial && initial.filter ? initial.filter : []));
    const [columnSort, setColumnSort] = useState(initial && initial.sort ? initial.sort : []);
    const [sizing, setSizing] = useState({});
    const [appliedPresetKeys, setAppliedPresetKeys] = useState(() => (initial?.filter?.length ? [] : (initialActivePresetsKeys ?? [])));
    if (!storageKey) {
        throw new Error('Table: storageKey must be a non-empty string');
    }
    // Fix localization for PT
    const localization = MRT_Localization_PT;
    localization.noRecordsToDisplay = 'Não há registos a serem exibidos';
    const sortedRef = React.useRef([]);
    useEffect(() => {
        const next = columnSort.map(s => ({ id: s.id, desc: s.desc }));
        if (JSON.stringify(next) !== JSON.stringify(sortedRef.current)) {
            sortedRef.current = next;
            onSort(next);
        }
    }, [columnSort, onSort]);
    // is the object in localStorage the full TablePrefs shape?
    const isValidPrefs = (obj) => {
        if (!obj || typeof obj !== 'object')
            return false;
        if (!obj.visibility || typeof obj.visibility !== 'object')
            return false;
        if (!obj.size || typeof obj.size !== 'object')
            return false;
        if (!Array.isArray(obj.order))
            return false;
        // if (!Array.isArray(obj.sorting)) return false;
        if (!obj.order.every((k) => typeof k === 'string'))
            return false;
        // if (!obj.sorting.every((s: any) => s && typeof s.id === 'string' && typeof s.desc === 'boolean')) return false;
        return true;
    };
    const loadPrefs = React.useCallback((cols) => {
        const raw = tryParse(localStorage.getItem(storageKey));
        const base = isValidPrefs(raw) ? raw : loadFresh(cols); // bad/missing -> defaults
        const next = normalize(base, cols); // heal for added/removed columns
        setSizing(next.size);
        localStorage.setItem(storageKey, JSON.stringify(next));
        return next;
    }, [storageKey]);
    const savePrefs = React.useCallback((prefs) => {
        localStorage.setItem(storageKey, JSON.stringify(prefs));
    }, [storageKey]);
    // Load once (handles migration)
    const [prefs, setPrefs] = React.useState(() => loadPrefs(columns));
    // Heal if columns change (add/remove/rename)
    useEffect(() => {
        const raw = tryParse(localStorage.getItem(storageKey));
        const base = isValidPrefs(raw) ? raw : loadFresh(columns);
        const next = normalize(base, columns);
        setPrefs(next);
        savePrefs(next);
    }, [storageKey, columns, savePrefs]);
    // Handy updater
    const updatePrefs = React.useCallback((patch) => {
        setPrefs(prev => {
            const next = { ...prev, ...patch };
            savePrefs(next);
            return next;
        });
    }, [savePrefs]);
    const prevFiltersRef = React.useRef('');
    /**
     * MATERIAL REACT TABLE with "Quick Filter Presets"
     *
     * Mental model
     * ------------
     * We have two sources of filters:
     *   1) User filters the table sets in the UI (`columnFilter` state)
     *   2) Preset filters the toolbar injects (via `quickFilterPresets`)
     *
     * We never store preset filters inside `columnFilter`.
     * Instead, at render time we build a "hydrated" view:
     *   UI (MRT) sees:     user filters + injected preset filters
     *   Stored state:      only user filters
     *
     * While a preset is active:
     *   - Its ids are "enforced": user edits to those ids either keep the preset (if it still matches)
     *     or drop the preset (if the user changed a preset value/op).
     *
     * Booleans & checkboxes
     * ---------------------
     * - For the UI: we coerce preset checkbox values to string literals 'true'/'false'
     *   so MRT checkboxes render correctly and change detection is stable.
     * - For comparisons: we normalize to '1'/'0' (see normalizeForCompare)
     *   so boolean-ish values compare predictably (true/1/'true' → '1').
     * - For the server: we convert to numeric 0/1 in the "BUILD EFFECTIVE FILTERS" effect.
     *
     * Autocomplete SELECT/MULTI_SELECT
     * --------------------------------
     * - Presets may pass primitives (number/boolean/string). We show the correct labels by
     *   resolving them against `filterSelectOptions` in `muiFilterAutocompleteProps`.
     *
     * Columns vs cols
     * ---------------
     * - We mutate column definitions during the setup effect (types, variants, props).
     *   Use `cols` (the finalized definitions) when reading `filterVariant` etc.
     */
    // Returns the default operator (fn) for a column id based on server config.
    // TEXT defaults to 'contains', everything else to 'equals'.
    const getDefaultFilterFn = React.useCallback((id) => {
        const def = options?.filter?.find((f) => f.name === id);
        return def?.type?.toUpperCase() === PaginationFilterType.TEXT ? 'contains' : 'equals';
    }, [options]);
    // Builds a map { [columnId]: defaultFilterFn } for all filterable columns.
    const buildDefaultFilterFns = React.useCallback(() => {
        const map = {};
        (options?.filter ?? []).forEach((f) => {
            map[f.name] = getDefaultFilterFn(f.name);
        });
        return map;
    }, [options, getDefaultFilterFn]);
    useEffect(() => {
        // First time, set all column filters as equal to avoid undefined
        if (!Object.keys(columnFilterFns).length && (options?.filter?.length ?? 0) > 0) {
            setColumnFilterFns(buildDefaultFilterFns());
        }
        columns.forEach(r => {
            r.enableColumnFilter = false;
        });
        // Filter Validation for each collumn
        if (options.filter) {
            const keys = options.filter;
            for (let i = 0; i < keys.length; i++) {
                const column = columns.find(r => r.accessorKey == keys[i].name);
                if (column != undefined) {
                    column.enableColumnFilter = true;
                    // column.filterFn = 'equals';
                    if (keys[i].type.toUpperCase() == PaginationFilterType.BOOLEAN) {
                        column.filterVariant = 'checkbox';
                    }
                    if (keys[i].type.toUpperCase() == PaginationFilterType.TEXT) {
                        column.columnFilterModeOptions = ['equals', 'notEquals', 'contains', 'startsWith', 'empty', 'notEmpty'];
                    }
                    if (keys[i].type.toUpperCase() == PaginationFilterType.NUMBER) {
                        column.columnFilterModeOptions = ['equals', 'notEquals', 'greaterThan', 'greaterThanOrEqualTo', 'lessThan', 'lessThanOrEqualTo'];
                    }
                    if (keys[i].type.toUpperCase() == PaginationFilterType.DATE) {
                        column.columnFilterModeOptions = ['equals', 'notEquals', 'greaterThan', 'greaterThanOrEqualTo', 'lessThan', 'lessThanOrEqualTo', 'empty', 'notEmpty'];
                        if (keys[i].options && keys[i].options.format) {
                            if (keys[i].options.format == 'YYYY-MM-DD') {
                                column.filterVariant = 'date';
                            }
                            else if (keys[i].options.format == 'HH:mm') {
                                column.filterVariant = 'time';
                            }
                            else {
                                column.filterVariant = 'datetime';
                            }
                        }
                        else {
                            column.filterVariant = 'datetime';
                        }
                    }
                    /* FUTURAMENTE
                    if (keys[i].type.toUpperCase() == PaginationFilterType.DATE) {
                      column.columnFilterModeOptions = ['equals', 'notEquals', 'greaterThan', 'greaterThanOrEqualTo', 'lessThan', 'lessThanOrEqualTo', 'empty', 'notEmpty'];
                      column.filterVariant = 'date';
                    }
                    if (keys[i].type.toUpperCase() == PaginationFilterType.DATETIME) {
                      column.columnFilterModeOptions = ['equals', 'notEquals', 'greaterThan', 'greaterThanOrEqualTo', 'lessThan', 'lessThanOrEqualTo', 'empty', 'notEmpty'];
                      column.filterVariant = 'datetime';
                    }
                    if (keys[i].type.toUpperCase() == PaginationFilterType.TIME) {
                      column.columnFilterModeOptions = ['equals', 'notEquals', 'greaterThan', 'greaterThanOrEqualTo', 'lessThan', 'lessThanOrEqualTo', 'empty', 'notEmpty'];
                      column.filterVariant = 'time';
                    }*/
                    if (keys[i].type.toUpperCase() == PaginationFilterType.SELECT) {
                        column.columnFilterModeOptions = [];
                        column.filterVariant = 'autocomplete';
                        column.muiFilterAutocompleteProps = {
                            freeSolo: true,
                            noOptionsText: 'Nenhuma opção disponível!',
                            // Prevents cases with same labels (should not happen but...)
                            getOptionLabel: (opt) => {
                                // allow presets to pass primitives (number/boolean/string)
                                if (typeof opt === 'string' || typeof opt === 'number' || typeof opt === 'boolean') {
                                    const hit = column.filterSelectOptions?.find((o) => String(o?.value) === String(opt));
                                    return hit?.label ?? String(opt);
                                }
                                return opt?.label ?? String(opt?.value ?? '');
                            },
                            // compare by id/value, not label
                            isOptionEqualToValue: (opt, val) => String(typeof opt === 'object' ? opt?.value : opt) === String(typeof val === 'object' ? (val?.value ?? val) : val),
                            // force a unique React key for each <li>
                            renderOption: (props, option) => (<li {...props} key={String(typeof option === 'string' ? option : option.value)}>
                  {typeof option === 'string' ? option : option.label}
                </li>)
                        };
                        if (column.filterSelectOptions == undefined) {
                            throw new Error('Por favor especifique a propriedade filterSelectOptions na definição da coluna ' + column.accessorKey);
                        }
                        else {
                            column.filterSelectOptions.sort((a, b) => a.label.localeCompare(b.label));
                        }
                    }
                    if (keys[i].type.toUpperCase() == PaginationFilterType.MULTI_SELECT) {
                        column.columnFilterModeOptions = [];
                        //column.filterVariant = 'multi-select';
                        column.filterVariant = 'autocomplete';
                        // Autocomplete presets may pass primitives (number/boolean/string).
                        // getOptionLabel resolves the label for those primitives via `filterSelectOptions`.
                        // isOptionEqualToValue compares by underlying value (stringified) so equality is stable
                        // across types (e.g., 1 vs '1').
                        column.muiFilterAutocompleteProps = {
                            multiple: true,
                            noOptionsText: 'Nenhuma opção disponível!',
                            getOptionLabel: (opt) => {
                                if (typeof opt === 'string' || typeof opt === 'number' || typeof opt === 'boolean') {
                                    const hit = column.filterSelectOptions?.find((o) => String(o?.value) === String(opt));
                                    return hit?.label ?? String(opt);
                                }
                                return opt?.label ?? String(opt?.value ?? '');
                            },
                            isOptionEqualToValue: (opt, val) => String(typeof opt === 'object' ? opt?.value : opt) === String(typeof val === 'object' ? (val?.value ?? val) : val),
                            renderOption: (props, option) => (<li {...props} key={String(typeof option === 'string' ? option : option.value)}>
                  {typeof option === 'string' ? option : option.label}
                </li>)
                        };
                        if (column.filterSelectOptions == undefined) {
                            throw new Error('Por favor especifique a propriedade filterSelectOptions na definição da coluna ' + column.accessorKey);
                        }
                        else {
                            column.filterSelectOptions.sort((a, b) => a.label.localeCompare(b.label));
                        }
                    }
                    if (keys[i].type.toUpperCase() == PaginationFilterType.DATE_RANGE) {
                        column.columnFilterModeOptions = ['between', 'empty', 'notEmpty'];
                        if (keys[i].options && keys[i].options.format) {
                            if (keys[i].options.format == 'YYYY-MM-DD') {
                                column.filterVariant = 'date-range';
                            }
                            else if (keys[i].options.format == 'HH:mm') {
                            }
                            else {
                                column.filterVariant = 'datetime-range';
                            }
                        }
                        else {
                            column.filterVariant = 'datetime-range';
                        }
                    }
                }
            }
            const result = [];
            Array.prototype.push.apply(result, columns);
            setCols(result);
        }
        columns.forEach(r => {
            r.enableSorting = false;
        });
        if (options.sort) {
            const keySort = options.sort;
            const sortedColumns = columns.map(col => {
                if (keySort.includes(col.accessorKey)) {
                    col.enableSorting = true;
                    let a = { sortable: true };
                    const k = Object.keys(col);
                    const v = Object.values(col);
                    for (let i = 0; i < Object.keys(col).length; i++) {
                        a[k[i]] = v[i];
                    }
                    return a;
                }
                return col;
            });
            setCols(sortedColumns);
        }
    }, [options, columns, buildDefaultFilterFns]);
    const getSelectedIds = () => {
        // if id is non numeric (ex. uuid, should also work, adding "" to the string)
        let s = Object.entries(selected).map(([k, v]) => [isNaN(Number(k)) ? `"${k}"` : Number(k), v]);
        let r = [];
        s.forEach(row => {
            if (row[1] == true) {
                r.push(row[0]);
            }
        });
        return r;
    };
    // Normalizes a UI value into a stable string so we can compare "did it change?"
    // - moment → ISO-like string
    // - autocomplete → compare by underlying value(s), order-insensitive for arrays
    // - booleans → '1' or '0' (true/1/'true' → '1', false/0/'false' → '0')
    // - everything else → String(value)
    const normalizeForCompare = (id, raw) => {
        const col = cols.find(c => c.accessorKey === id || c.id === id);
        let v = raw;
        if (v == null)
            return '';
        // moment -> ISO-like
        if (moment.isMoment(v))
            return v.format();
        // autocomplete (single/multi) may hold objects
        if (col?.filterVariant === 'autocomplete') {
            if (Array.isArray(v)) {
                return v
                    .map(x => (typeof x === 'object' ? (x?.value ?? x?.id ?? String(x)) : String(x)))
                    .sort() // order-insensitive for multi
                    .join(',');
            }
            if (typeof v === 'object')
                return String(v?.value ?? v?.id ?? '');
        }
        // NEW: normalize boolean-ish to '1' | '0' ---
        if (v === 'true')
            v = true;
        if (v === 'false')
            v = false;
        if (v === 1 || v === 0)
            v = Boolean(v);
        if (typeof v === 'boolean')
            return v ? '1' : '0';
        return String(v);
    };
    // Returns true if the current UI filter state still matches *all* filters of a preset,
    // using current operators (fns) for each column. Special cases: 'empty'/'notEmpty' ignore value.
    const presetMatches = (preset, filters, fns) => preset.filters.every(p => {
        const f = filters.find(x => x.id === p.id);
        if (!f)
            return false;
        const op = fns[p.id] || 'equals';
        if (op !== p.type)
            return false;
        if (p.type === 'empty' || p.type === 'notEmpty')
            return true;
        return normalizeForCompare(p.id, f.value) === normalizeForCompare(p.id, p.value);
    });
    // applyPreset: drop any existing user filters on the same ids, set the preset's operators,
    // and mark the preset as active.
    const applyPreset = (preset) => {
        const ids = new Set(preset.filters.map(p => p.id));
        // keep only non-preset user filters in state
        setColumnFilter(prev => prev.filter(f => !ids.has(f.id)));
        // set/merge operators: start from defaults, then prev, then preset
        setColumnFilterFns(prev => {
            const fns = { ...buildDefaultFilterFns(), ...prev };
            preset.filters.forEach(p => {
                fns[p.id] = p.type;
            });
            return fns;
        });
        setAppliedPresetKeys([preset.key]);
    };
    // removePreset: unset the active preset and restore default operators for those ids.
    const removePreset = (preset) => {
        const ids = new Set(preset.filters.map(p => p.id));
        setColumnFilterFns(prev => {
            const next = { ...prev };
            ids.forEach(id => {
                next[id] = getDefaultFilterFn(id);
            });
            return next;
        });
        setAppliedPresetKeys([]);
    };
    // togglePreset: turn the preset on/off.
    const togglePreset = (preset) => {
        const active = appliedPresetKeys.includes(preset.key);
        return active ? removePreset(preset) : applyPreset(preset);
    };
    const activePreset = React.useMemo(() => (appliedPresetKeys.length === 1 ? quickFilterPresets.find(p => p.key === appliedPresetKeys[0]) : undefined), [appliedPresetKeys, quickFilterPresets]);
    const enforcedIds = React.useMemo(() => new Set(activePreset?.filters.map(f => f.id) ?? []), [activePreset]);
    // helper: is this id a BOOLEAN filter by config?
    const isBooleanFilterId = (id) => (options?.filter ?? []).some((f) => f?.name === id && String(f?.type).toUpperCase() === PaginationFilterType.BOOLEAN);
    // Coerces a preset value into what MRT's *UI controls* expect to render.
    // - For checkbox/boolean filters: we intentionally return 'true' | 'false' (strings).
    //   This keeps the checkbox visuals in sync and makes onChange deltas reliable.
    // - For other variants (incl. autocomplete): return raw; label resolution happens
    //   in the column's `muiFilterAutocompleteProps`.
    const coercePresetValueForUI = (id, raw) => {
        const col = cols.find(c => c.accessorKey === id || c.id === id);
        if (!col)
            return raw;
        // Checkbox filter expects boolean
        if (col.filterVariant === 'checkbox' || isBooleanFilterId(id)) {
            if (raw === true || raw === 'true' || raw === 1 || raw === '1')
                return 'true';
            if (raw === false || raw === 'false' || raw === 0 || raw === '0')
                return 'false';
            return Boolean(raw) ? 'true' : 'false';
        }
        // Autocomplete & others can stay as-is (we'll fix labels in step 2)
        return raw;
    };
    // What MRT should display: the "hydrated" view = user filters + injected preset filters.
    // We hide preset-controlled ids from stored state; they are injected here at render time.
    // This is also the basis for what we later convert to server filters.
    const uiColumnFilters = React.useMemo(() => {
        const user = columnFilter.filter(f => !enforcedIds.has(f.id));
        const preset = activePreset
            ? activePreset.filters.map(p => ({
                id: p.id,
                value: coercePresetValueForUI(p.id, p.value)
            }))
            : [];
        return [...user, ...preset];
    }, [columnFilter, enforcedIds, activePreset, cols, options]);
    // Operators used by MRT in the UI: start from defaults + current state,
    // and override with the active preset's operators so the UI reflects the preset.
    const uiColumnFilterFns = React.useMemo(() => {
        const base = { ...buildDefaultFilterFns(), ...columnFilterFns };
        if (activePreset) {
            activePreset.filters.forEach(p => {
                base[p.id] = p.type;
            });
        }
        return base;
    }, [columnFilterFns, buildDefaultFilterFns, activePreset]);
    // Converts the hydrated UI filters into the backend payload:
    // - 'empty'/'notEmpty' → send type with empty value
    // - checkbox/boolean → normalize to 0/1
    // - autocomplete → extract the primitive selected value (and ignore invalids)
    // - moment → format to string
    // - drop truly empty values
    // Finally, only fire `onFilter` when the serialized payload actually changed (debounces churn).
    useEffect(() => {
        const aux = (uiColumnFilters || [])
            .map(filter => {
            const column = cols.find(c => c.accessorKey === filter.id || c.id === filter.id);
            const filterFn = uiColumnFilterFns[filter.id] || 'equals';
            // empty / notEmpty never need a value
            if (filterFn === 'empty' || filterFn === 'notEmpty') {
                return { id: filter.id, value: '', type: filterFn };
            }
            let value = filter.value;
            // Normalize checkbox/boolean to 0/1 for the backend
            if (column?.filterVariant === 'checkbox' || isBooleanFilterId(filter.id)) {
                if (value === true || value === 'true' || value === 1 || value === '1')
                    value = 1;
                else if (value === false || value === 'false' || value === 0 || value === '0')
                    value = 0;
                else
                    value = Number(Boolean(value)); // defensive fallback
            }
            // Existing Autocomplete normalization (single select)
            if (column?.filterVariant === 'autocomplete') {
                // ignore empty
                if (value == null || (Array.isArray(value) && value.length === 0) || String(value).trim() === '') {
                    return undefined;
                }
                const picked = typeof value === 'object' && !Array.isArray(value) ? value.value : value;
                const exists = column.filterSelectOptions?.some((opt) => opt?.value === picked);
                if (!exists)
                    return undefined;
                value = picked;
            }
            // Ignore truly empty values
            if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) {
                return undefined;
            }
            // Deal with dates
            if (moment.isMoment(value))
                value = value.format();
            return { id: filter.id, value: String(value), type: filterFn };
        })
            .filter(Boolean);
        const serialized = JSON.stringify(aux);
        if (serialized !== prevFiltersRef.current) {
            prevFiltersRef.current = serialized;
            onFilter(aux);
        }
    }, [uiColumnFilters, uiColumnFilterFns, cols, onFilter]);
    return (<LocalizationProvider dateAdapter={AdapterMoment}>
      <FieldsDialog open={open} columns={columns} values={prefs.visibility} onSubmit={nextVisibility => {
            updatePrefs({ visibility: nextVisibility });
        }} close={() => setOpen(false)}/>
      <MaterialReactTable key={appliedPresetKeys.join('|')} 
    // LEFT side of the top toolbar
    renderTopToolbarCustomActions={({ table }) => (<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {/* Built-in quick filter preset buttons */}
            {quickFilterPresets?.length ? (<Box sx={{ display: 'flex', gap: 1, mr: 1 }}>
                {quickFilterPresets.map(p => {
                    const active = appliedPresetKeys.includes(p.key);
                    const title = active ? 'Desativar filtro' : p.tooltip || 'Ativar filtro';
                    return (<Tooltip key={p.key} title={title}>
                      <Button size='small' variant={active ? 'contained' : 'outlined'} onClick={() => togglePreset(p)}>
                        {p.label}
                      </Button>
                    </Tooltip>);
                })}
              </Box>) : null}

            {/* Caller-provided left-side actions (preserved) */}
            {toolbarCustomActions?.(table)}
          </Box>)} renderToolbarInternalActions={({ table }) => !!selectable && !!selectActions && getSelectedIds().length > 0 ? (selectActions(getSelectedIds())) : (<Box>
              {customTableOption ? customTableOption(table) : <></>}
              {(uiColumnFilters?.length ?? 0) > 0 && (<Tooltip title='Limpar filtro(s) aplicado(s)'>
                  <IconButton onClick={() => {
                    setColumnFilter([]);
                    setColumnFilterFns(buildDefaultFilterFns());
                    setAppliedPresetKeys([]);
                }}>
                    <MdiIcon path={mdiFilterOff}/>
                  </IconButton>
                </Tooltip>)}
              <Tooltip title='Mostrar/ocultar filtros'>
                <IconButton onClick={() => {
                setShowFilter(!showFilter);
            }}>
                  <MdiIcon path={showFilter ? mdiFilterVariantRemove : mdiFilterVariant}/>
                </IconButton>
              </Tooltip>
              <Tooltip title='Mostrar/ocultar colunas'>
                <IconButton onClick={() => {
                setOpen(true);
            }}>
                  <MdiIcon path={mdiViewColumn}/>
                </IconButton>
              </Tooltip>
              {onExport && (<Tooltip title='Exportar'>
                  <IconButton onClick={() => {
                    onExport();
                }}>
                    <MdiIcon path={mdiDownload}/>
                  </IconButton>
                </Tooltip>)}
            </Box>)} getRowId={row => {
            const a = row;
            return a.id;
        }} columns={cols} data={data.data} rowCount={data.meta.totalItems ?? 0} pageCount={data.meta.totalPages ?? 0} state={{
            density: 'compact',
            rowSelection: selected,
            pagination: {
                pageIndex: data.meta.currentPage > 0 ? data.meta.currentPage - 1 : 0,
                pageSize: data.meta.itemsPerPage
            },
            columnFilters: uiColumnFilters,
            columnFilterFns: uiColumnFilterFns,
            isLoading,
            showColumnFilters: showFilter,
            columnVisibility: prefs.visibility,
            columnOrder: prefs.order,
            columnSizing: prefs.size,
            // sorting: prefs.sorting,
            sorting: columnSort.map(sort => ({ id: sort.id, desc: sort.desc }))
        }} localization={localization} muiCircularProgressProps={{
            sx: {
                color: '#049a9b'
            },
            thickness: 5,
            size: 55
        }} muiFilterCheckboxProps={{
            size: 'small'
        }} muiTableBodyCellProps={({ row }) => ({
            onDoubleClick: () => {
                onDoubleClick?.(row.original);
            },
            sx: {
                cursor: 'pointer' //you might want to change the cursor too when adding an onClick
            }
        })} manualPagination={true} onPaginationChange={onPaginate} manualSorting={true} onSortingChange={next => {
            setColumnSort(next);
            // updatePrefs({ sorting: next as SortProperties[] });
            onSort?.(next);
        }} onColumnSizingChange={updater => {
            const next = typeof updater === 'function' ? updater(prefs.size) : updater;
            console.log(next);
            updatePrefs({ size: next });
        }} enableColumnResizing={true} enableMultiRowSelection enableSelectAll={true} selectAllMode='page' enableRowSelection={selectable != undefined ? row => selectable(row) : false} enableBatchRowSelection={selectable != undefined} onRowSelectionChange={setSelected} layoutMode='grid' enableRowActions={true} enableStickyHeader={true} enableMultiSort={true} enableColumnFilters={true} enableColumnFilterModes={true} enableColumnOrdering={true} onColumnOrderChange={newOrder => {
            updatePrefs({ order: newOrder });
        }} 
    // If the user changes the operator (e.g., equals → notEquals) on a preset-controlled id,
    // we "break" the preset:
    //   - drop the preset key
    //   - reset *other* preset ids' operators back to their defaults
    // If no preset is active, behave normally.
    onColumnFilterFnsChange={next => {
            setColumnFilterFns(prev => {
                const incoming = typeof next === 'function' ? next(prev) : next;
                const merged = { ...prev, ...incoming };
                const active = appliedPresetKeys.length === 1 ? quickFilterPresets.find(p => p.key === appliedPresetKeys[0]) : undefined;
                if (!active)
                    return merged;
                let broke = false;
                const changedIdList = [];
                for (const p of active.filters) {
                    const op = merged[p.id];
                    if (op && op !== p.type) {
                        broke = true;
                        changedIdList.push(p.id);
                    }
                }
                if (!broke)
                    return merged;
                // drop preset & reset other preset ops to defaults
                setAppliedPresetKeys([]);
                const changedIds = new Set(changedIdList);
                for (const p of active.filters) {
                    if (!changedIds.has(p.id)) {
                        merged[p.id] = getDefaultFilterFn(p.id);
                    }
                }
                return merged;
            });
        }} manualFiltering={true} 
    // The tricky bit. MRT calls this with either an array or an updater function.
    // When a preset is active:
    //   1) Build a "hydrated" list = user filters + injected preset filters as the UI sees them.
    //   2) Run the incoming updater against the hydrated list.
    //   3) If the user didn't touch any preset-controlled id → store result (no change to preset).
    //   4) If the result still exactly matches the preset → keep preset; hide those ids from state.
    //   5) Else (user broke the preset):
    //        - drop the preset key
    //        - reset operators for preset ids the user did NOT change back to defaults
    //        - keep only (a) non-preset filters and (b) preset filters that the user changed,
    //          so they remain visible & editable as normal user filters
    onColumnFiltersChange={updater => {
            setColumnFilter(prev => {
                // 1) Identify active preset (if any)
                const active = appliedPresetKeys.length === 1 ? quickFilterPresets.find(p => p.key === appliedPresetKeys[0]) : undefined;
                if (!active) {
                    // No preset active → behave normally
                    return typeof updater === 'function' ? updater(prev) : updater;
                }
                const presetIds = new Set(active.filters.map(f => f.id));
                // 2) Build a **hydrated** view = user filters + injected preset filters (as UI sees them)
                const hydrated = [
                    ...prev.filter(f => !presetIds.has(f.id)),
                    ...active.filters.map(p => ({
                        id: p.id,
                        value: coercePresetValueForUI(p.id, p.value)
                    }))
                ];
                // 3) Apply MRT’s updater to the hydrated view
                const proposed = typeof updater === 'function' ? updater(hydrated) : updater;
                // If the user didn't touch any preset-controlled id, keep behaving normally
                const touchedPreset = proposed.some(f => presetIds.has(f.id));
                if (!touchedPreset)
                    return proposed;
                // 4) Use **hydrated fns** (defaults + current + preset overrides)
                const hydratedFns = (() => {
                    const base = { ...buildDefaultFilterFns(), ...columnFilterFns };
                    active.filters.forEach(p => {
                        base[p.id] = p.type;
                    });
                    return base;
                })();
                // 5) If after the change the preset still matches exactly, keep it active
                const stillMatches = presetMatches(active, proposed, hydratedFns);
                if (stillMatches) {
                    // hide preset-controlled ids from stored state
                    return proposed.filter(f => !presetIds.has(f.id));
                }
                // 6) Preset is broken → drop it, but keep user-modified preset fields visible,
                //    and reset operators for untouched preset fields back to defaults
                setAppliedPresetKeys([]);
                const changedIds = new Set();
                for (const p of active.filters) {
                    const f = proposed.find(x => x.id === p.id);
                    if (!f)
                        continue; // removed is also a change; we won't re-add it
                    const same = normalizeForCompare(p.id, f.value) === normalizeForCompare(p.id, p.value);
                    if (!same)
                        changedIds.add(p.id);
                }
                setColumnFilterFns(prevFns => {
                    const next = { ...prevFns };
                    for (const p of active.filters) {
                        if (!changedIds.has(p.id)) {
                            next[p.id] = getDefaultFilterFn(p.id);
                        }
                    }
                    return next;
                });
                // Keep:
                //   • all non-preset filters, and
                //   • only the preset fields that the user actually changed (so they stay visible/editable)
                return proposed.filter(f => !presetIds.has(f.id) || changedIds.has(f.id));
            });
        }} enableColumnActions={false} positionActionsColumn='last' displayColumnDefOptions={{
            'mrt-row-actions': {
                size: rowActionSize ? rowActionSize : 70,
                header: '',
                grow: false
            },
            'mrt-row-select': {
                grow: false
            }
        }} renderRowActionMenuItems={cellActionItems} renderRowActions={actionItems} muiTableBodyRowProps={({ row }) => (withRowStyle ? withRowStyle(row.original) : {})}/>
    </LocalizationProvider>);
}
